/**
 * Shared lab-routing helpers for the per-test lab selectors — the Picker-stack `MaterialsTestLabPicker` and the
 * legacy select2 `MaterialsTestLabSelector`, which differ in how they mount but route labs identically.
 *
 * Two concerns live here:
 *
 *   - **promotion.** The per-test labs also tell the sample where to ship. A sample ships to a PARENT lab, so
 *     the sample-level selector holds the parent of each test's lab (a lab with no parent stands for itself).
 *     Labs held only because a test promoted them are tracked, so they leave again once no test calls for
 *     them; a lab the user chose is never removed.
 *   - **auto-fill.** A test row that arrives with no lab takes the resolver's answer — the only lab that can
 *     run the test, else the top of its preference ranking — and stays blank when there is none. Never
 *     overwrites a value that is already there.
 *
 * @module _LabRouting
 */

const SAMPLE_LAB_SYNC_EVENT = 'SampleLabSync';
const DEFAULT_TEST_INSTANCES_ADDRESS = 'AppData.DocumentData.ReportData.FormData.SampleTestIDs__Record_Instances';

/**
 * The lab id the sample-level selector should hold for a lab picked against a test: its parent, or itself when
 * it has none. Falls back to the record's own `IDLabParent` when the resolver has not primed, and to the lab
 * itself when neither answers — promotion must never drop the selection.
 *
 * @param {Object} pPict - the pict instance (reads `providers.LabScopeResolver`).
 * @param {Record<string, any>} pLab - the picked Lab record.
 * @return {number} The IDLab to promote, or 0 when there is nothing to promote.
 */
function resolveSampleLabID(pPict, pLab)
{
	const tmpIDLab = parseInt(pLab && pLab.IDLab, 10);
	if (!(tmpIDLab > 0))
	{
		return 0;
	}
	const tmpResolved = parseInt(pPict?.providers?.LabScopeResolver?.getParentLabID?.(tmpIDLab), 10);
	if (tmpResolved > 0)
	{
		return tmpResolved;
	}
	const tmpRecordParent = parseInt(pLab.IDLabParent, 10);
	return (tmpRecordParent > 0) ? tmpRecordParent : tmpIDLab;
}

/**
 * A lab selection value (csv, array or empty) as trimmed id strings, blanks dropped.
 *
 * @param {any} pValue
 * @return {Array<string>}
 */
function parseLabIDList(pValue)
{
	if (pValue === null || pValue === undefined || pValue === '')
	{
		return [];
	}
	const tmpEntries = Array.isArray(pValue) ? pValue : String(pValue).split(',');
	return tmpEntries.map((pEntry) => String(pEntry).trim()).filter((pEntry) => pEntry.length > 0);
}

/**
 * The sample-level labs the per-test labs call for right now: the parent of each row's lab, in row order.
 *
 * @param {Object} pPict - the pict instance.
 * @param {Array<Record<string, any>>} pTriples - the Test selector's TestInstance triples.
 * @return {Array<string>}
 */
function collectPromotedSampleLabIDs(pPict, pTriples)
{
	const tmpWanted = [];
	for (const tmpTriple of (Array.isArray(pTriples) ? pTriples : []))
	{
		const tmpTestInstance = tmpTriple && tmpTriple.TestInstance;
		const tmpIDLab = parseInt(tmpTestInstance && tmpTestInstance.IDLab, 10);
		if (!(tmpIDLab > 0))
		{
			continue;
		}
		const tmpRecord = tmpTestInstance.IDLab__Record;
		const tmpLab = (tmpRecord && parseInt(tmpRecord.IDLab, 10) === tmpIDLab) ? tmpRecord : { IDLab: tmpIDLab };
		const tmpSampleLabID = String(resolveSampleLabID(pPict, tmpLab));
		if (tmpWanted.indexOf(tmpSampleLabID) < 0)
		{
			tmpWanted.push(tmpSampleLabID);
		}
	}
	return tmpWanted;
}

/**
 * The labs the sample's tests are assigned to right now, in row order — the ranking prefers them so a row
 * does not add a lab to the sample when one already in use can run its test.
 *
 * @param {Object} pPict - the pict instance.
 * @return {Array<number>}
 */
function collectSelectedTestLabIDs(pPict)
{
	const tmpTriples = pPict?.manifest?.getValueAtAddress?.(pPict, DEFAULT_TEST_INSTANCES_ADDRESS);
	const tmpSelected = [];
	for (const tmpTriple of (Array.isArray(tmpTriples) ? tmpTriples : []))
	{
		const tmpIDLab = parseInt(tmpTriple && tmpTriple.TestInstance && tmpTriple.TestInstance.IDLab, 10);
		if (tmpIDLab > 0 && tmpSelected.indexOf(tmpIDLab) < 0)
		{
			tmpSelected.push(tmpIDLab);
		}
	}
	return tmpSelected;
}

/**
 * Reconcile the sample-level lab selection with the labs the tests call for. A called-for lab missing from the
 * selection is added and recorded as promoted; a promoted lab no test calls for any more is removed; a lab the
 * user chose is never touched, and one the user removed stops counting as promoted.
 *
 * @param {Array<string>} pSelection - the current selection, in order.
 * @param {Array<string>} pPromoted - the part of the selection promotion put there.
 * @param {Array<string>} pWanted - the labs the tests call for now.
 * @return {{ Selection: Array<string>, Promoted: Array<string> }}
 */
function reconcileSampleLabs(pSelection, pPromoted, pWanted)
{
	const tmpWanted = new Set(pWanted);
	const tmpPromoted = new Set(pPromoted.filter((pID) => pSelection.indexOf(pID) > -1));
	const tmpSelection = pSelection.filter((pID) => !tmpPromoted.has(pID) || tmpWanted.has(pID));
	const tmpStillPromoted = Array.from(tmpPromoted).filter((pID) => tmpWanted.has(pID));
	for (const tmpID of pWanted)
	{
		if (tmpSelection.indexOf(tmpID) < 0)
		{
			tmpSelection.push(tmpID);
			tmpStillPromoted.push(tmpID);
		}
	}
	return { Selection: tmpSelection, Promoted: tmpStillPromoted };
}

/**
 * Bring a sample-level lab selector in line with the per-test labs (see reconcileSampleLabs). Reads the test
 * triples from `PictForm.TestInstancesAddress` (default: the Test selector's) and keeps the promoted set in the
 * form data at `pPromotedAddress`, so a retraction still knows what it may remove after a reopen. A document
 * saved before that set existed carries none, so nothing on it counts as promoted and nothing is removed.
 *
 * @param {Object} pPict - the pict instance.
 * @param {Record<string, any>} pInput - the sample-level lab selector's descriptor.
 * @param {Object} pDestination - the view's marshal destination object.
 * @param {string} pPromotedAddress - where the promoted set lives, relative to pDestination.
 * @param {any} pCurrentValue - the selector's current value.
 * @return {string|null} The new selection csv, or null when it is unchanged.
 */
function syncSampleLabSelection(pPict, pInput, pDestination, pPromotedAddress, pCurrentValue)
{
	const tmpTriplesAddress = (pInput && pInput.PictForm && pInput.PictForm.TestInstancesAddress) || DEFAULT_TEST_INSTANCES_ADDRESS;
	const tmpTriples = pPict.manifest.getValueAtAddress(pPict, tmpTriplesAddress);
	const tmpSelection = parseLabIDList(pCurrentValue);
	const tmpResult = reconcileSampleLabs(tmpSelection, parseLabIDList(pPict.manifest.getValueAtAddress(pDestination, pPromotedAddress)), collectPromotedSampleLabIDs(pPict, tmpTriples));
	pPict.manifest.setValueAtAddress(pDestination, pPromotedAddress, tmpResult.Promoted.join(','));
	const tmpNextValue = tmpResult.Selection.join(',');
	return (tmpNextValue === tmpSelection.join(',')) ? null : tmpNextValue;
}

/**
 * Ask the sample-level lab selector to reconcile with the per-test labs. The per-test selectors hold no
 * reference to that input, so they broadcast; it owns its value and join array. Sent on every change to the
 * per-test labs — a clear, a re-pick or a removed test has to retract as surely as a pick promotes.
 *
 * @param {Object} pPict - the pict instance.
 * @return {void}
 */
function requestSampleLabSync(pPict)
{
	pPict?.views?.PictFormMetacontroller?.triggerGlobalInputEvent?.(SAMPLE_LAB_SYNC_EVENT);
}

/**
 * Whether a per-test lab selector should auto-fill. Opt out per descriptor with `PictForm.AutoFillLab: false`;
 * on by default, since a row whose lab is uniquely determined has nothing for the user to decide.
 *
 * @param {Record<string, any>} pInput - the form input descriptor.
 * @return {boolean}
 */
function isAutoFillEnabled(pInput)
{
	return (pInput && pInput.PictForm) ? (pInput.PictForm.AutoFillLab !== false) : true;
}

/**
 * The descriptor's per-test `LabScope` filter entry, which carries the axis addresses the resolver needs.
 *
 * @param {Record<string, any>} pInput - the form input descriptor.
 * @return {Record<string, any>|null}
 */
function getTestLabScopeFilter(pInput)
{
	const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
	if (!Array.isArray(tmpFilters))
	{
		return null;
	}
	for (const tmpFilter of tmpFilters)
	{
		if (tmpFilter && tmpFilter.LabScope === 'Test')
		{
			return tmpFilter;
		}
	}
	return null;
}

/**
 * The lab to fill into an empty per-test selector for a row, or 0 when there is nothing to fill. Resolves the
 * row's test through the same resolver the filters and pins use, so the auto-filled value is by construction
 * the first one the dropdown would have offered.
 *
 * @param {Object} pPict - the pict instance (reads `providers.LabScopeResolver` / `providers.EntityFilterEngine`).
 * @param {Record<string, any>} pInput - the form input descriptor.
 * @param {number|string|null} pRowIndex - the tabular row index.
 * @param {Object} pView - the form view (provides the marshal destination for the row read).
 * @return {number}
 */
function resolveAutoFillLabID(pPict, pInput, pRowIndex, pView)
{
	const tmpResolver = pPict?.providers?.LabScopeResolver;
	const tmpEngine = pPict?.providers?.EntityFilterEngine;
	if (!tmpResolver || !tmpEngine || !isAutoFillEnabled(pInput))
	{
		return 0;
	}
	// Show All means "let me pick anything" — filling a value under it would contradict the request.
	if (tmpEngine.isFilterOverridden(pInput, pRowIndex))
	{
		return 0;
	}
	const tmpFilter = getTestLabScopeFilter(pInput);
	if (!tmpFilter)
	{
		return 0;
	}
	const tmpIDTest = tmpEngine._readLabScopeTestID(tmpFilter, pInput, pRowIndex, pView);
	if (!tmpIDTest)
	{
		return 0;
	}
	return parseInt(tmpResolver.getAutoFillLabID(tmpIDTest, { SelectedLabIDs: collectSelectedTestLabIDs(pPict) }), 10) || 0;
}

/**
 * Fill an empty per-test selector row, waiting out a resolver prime that is still loading the row's test.
 *
 * The Testing table re-renders as soon as the test set changes, while the resolver re-primes for the new set
 * asynchronously — so the newest row is evaluated against caches that do not yet know its test, and nothing
 * revisits it. When the first attempt finds nothing and a prime is in flight, retry once it lands, provided
 * the row still holds the same test and is still empty.
 *
 * @param {Object} pPict - the pict instance.
 * @param {Record<string, any>} pInput - the form input descriptor.
 * @param {number|string} pRowIndex - the tabular row index.
 * @param {Object} pView - the form view.
 * @param {() => any} fReadRowLab - reads the row's current lab.
 * @param {(pIDLab: number) => void} fCommit - commits a lab through the selector's pick path.
 * @return {void}
 */
function autoFillTestLab(pPict, pInput, pRowIndex, pView, fReadRowLab, fCommit)
{
	const tmpIDLab = resolveAutoFillLabID(pPict, pInput, pRowIndex, pView);
	if (tmpIDLab > 0)
	{
		fCommit(tmpIDLab);
		return;
	}
	const tmpResolver = pPict?.providers?.LabScopeResolver;
	const tmpEngine = pPict?.providers?.EntityFilterEngine;
	const tmpFilter = getTestLabScopeFilter(pInput);
	if (!tmpResolver || !tmpEngine || !tmpFilter || typeof tmpResolver.primeFromForm !== 'function' || typeof tmpResolver.isPriming !== 'function')
	{
		return;
	}
	const tmpIDTest = tmpEngine._readLabScopeTestID(tmpFilter, pInput, pRowIndex, pView);
	if (!tmpIDTest)
	{
		return;
	}
	const tmpPrime = tmpResolver.primeFromForm();
	if (!tmpResolver.isPriming())
	{
		return;
	}
	tmpPrime.then(() =>
	{
		if (String(tmpEngine._readLabScopeTestID(tmpFilter, pInput, pRowIndex, pView)) !== String(tmpIDTest))
		{
			return;
		}
		if (parseInt(fReadRowLab(), 10) > 0)
		{
			return;
		}
		const tmpRetryIDLab = resolveAutoFillLabID(pPict, pInput, pRowIndex, pView);
		if (tmpRetryIDLab > 0)
		{
			fCommit(tmpRetryIDLab);
		}
	}).catch((pError) =>
	{
		pPict?.log?.warn?.(`[LabRouting] deferred lab auto-fill failed for row ${pRowIndex}: ${pError?.message || pError}`);
	});
}

/**
 * The spec-set lab floor both selector stacks apply to a Lab picker, family-expanded when the
 * LabScopeResolver is available to supply the lab tree.
 *
 * `LabTestSpecificationSetJoin` declares membership at the FAMILY level — the set names parent labs and
 * their benches are allowed through them — so raw `IDLab` membership empties any list of benches, and a
 * set that also names a bench directly empties the parent-level list. Falls back to raw membership when
 * no resolver is registered, which is the pre-existing behavior.
 *
 * @param {Object} pPict - the pict instance.
 * @return {Set<number>|null} The admitted lab ids, or null for no floor.
 */
function resolveSpecSetLabFloor(pPict)
{
	const tmpExpanded = pPict?.providers?.LabScopeResolver?.getSpecSetLabFloor?.();
	if (tmpExpanded)
	{
		return tmpExpanded;
	}
	return pPict?.providers?.SpecScopeResolver?.getAllowedLabIDs?.() || null;
}

module.exports =
{
	SAMPLE_LAB_SYNC_EVENT,
	resolveSampleLabID,
	parseLabIDList,
	collectPromotedSampleLabIDs,
	collectSelectedTestLabIDs,
	reconcileSampleLabs,
	syncSampleLabSelection,
	requestSampleLabSync,
	isAutoFillEnabled,
	getTestLabScopeFilter,
	resolveAutoFillLabID,
	autoFillTestLab,
	resolveSpecSetLabFloor,
};
